import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../components';
import ComplianceNotice from '../components/ComplianceNotice';
import './GoalPlanningEngine.css';

const COPY = {
  en: {
    title: 'Goal Planning Engine',
    subtitle:
      'Answer practical questions, estimate your goal plan, and review strategy with tangible numbers.',
    goalTypes: {
      retirement: 'Retirement',
      house: 'House Purchase',
      education: 'Education Savings',
      emergency: 'Emergency Fund',
      wealth: 'Wealth Accumulation',
    },
    riskOptions: ['Conservative', 'Moderate', 'Aggressive'],
    periodOptions: {
      biweekly: 'Biweekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
    },
    feasibilityStatus: {
      strong: 'Strongly feasible with current budget profile',
      tight: 'Possible but tight - may need tradeoffs',
      weak: 'Currently not feasible without changes',
    },
    labels: {
      goalType: 'Goal type',
      profileTitle: 'Goal profile',
      profileSubtitle: 'Choose a goal and answer only the questions relevant to that goal.',
      assumptionsTitle: 'Planning assumptions',
      assumptionsSubtitle: 'These assumptions shape the estimate and can be adjusted anytime.',
      currentAge: 'Current age',
      income: 'Annual income',
      householdIncome: 'Household annual income (optional)',
      savings: 'Current savings for this goal',
      investments: 'Current invested amount for this goal',
      targetAmount: 'Target amount',
      horizon: 'Time horizon (years)',
      yearsShort: 'years',
      risk: 'Risk tolerance',
      contributionPeriod: 'Contribution period',
      estimateNow: 'Estimate with live model',
      resultsTitle: 'Goal estimate results',
      resultsSubtitle: 'Detailed numbers and strategy recommendations based on your scenario.',
      hasTfsa: 'Have TFSA',
      hasRrsp: 'Have RRSP',
      hasFhsa: 'Have FHSA',
      hasResp: 'Have RESP',
      availableAccounts: 'Available account types',
      requiredMonthly: 'Required monthly equivalent savings',
      requiredPerPeriod: 'Required savings per selected period',
      progress: 'Current progress',
      targetGoal: 'Target goal amount',
      currentBase: 'Current base toward this goal',
      projectedBase: 'Projected value of current base at horizon',
      growthAssumption: 'Growth assumption',
      gapAtStart: 'Current funding gap',
      accountSuggestions: 'Suggested account types',
      milestones: 'Savings milestones',
      strategy: 'Strategy recommendations',
      assumptions: 'Model assumptions',
      assumptionsLineOne: 'Assumes constant growth and regular contributions.',
      assumptionsLineTwo: 'Educational estimate only; review with qualified professionals before decisions.',
      accountLowLiquidity: 'High-liquidity savings',
      accountNonRegistered: 'Non-registered account',
      detailsPrompt: 'Recommended account mix for this goal.',
      milestonesSubtitle: 'Target checkpoints from 25% to 100% completion.',
      estimate: 'Estimate with live model',
      estimating: 'Estimating...',
      saveGoal: 'Save goal',
      savingGoal: 'Saving...',
      saveSuccess: 'Goal saved to your planning profile.',
      saveLogin: 'Sign in to save goals.',
      apiError: 'Live planning model is unavailable. Showing local estimate.',
      resultLead: 'Estimated contribution target',
      homeCost: 'Home purchase price',
      downPaymentPct: 'Down payment percentage',
      downPaymentAmount: 'Estimated down payment target',
      mortgageRate: 'Mortgage interest rate (%)',
      amortizationYears: 'Mortgage amortization (years)',
      estimatedMortgagePayment: 'Estimated monthly mortgage payment',
      educationAnnualCost: 'Estimated annual education cost (today)',
      educationYearsUntilStart: 'Years until studies begin',
      educationYears: 'Length of studies (years)',
      educationInflation: 'Education inflation assumption (%)',
      expectedGrants: 'Expected grants / RESP support',
      inflationAdjustedEducationCost: 'Inflation-adjusted education cost target',
      monthlyEssentialExpenses: 'Essential monthly expenses',
      emergencyMonths: 'Emergency coverage target (months)',
      emergencyBuildYears: 'Years to build emergency fund',
      desiredRetirementIncome: 'Desired monthly retirement income',
      expectedGovBenefits: 'Expected monthly CPP/OAS/GIS income',
      safeWithdrawalRate: 'Withdrawal rate in retirement (%)',
      optionalFeasibility: 'Evaluate goal feasibility (optional)',
      optionalFeasibilitySubtitle:
        'Add household cash-flow data for a practical feasibility check and recommendations.',
      monthlyExpenses: 'Total monthly expenses',
      monthlyDebtPayments: 'Monthly debt payments',
      useHouseholdIncome: 'Use household income for feasibility',
      feasibilityTitle: 'Feasibility analysis',
      feasibilitySubtitle: 'Assesses whether your current budget can realistically support this goal contribution.',
      disposableIncome: 'Estimated monthly disposable income',
      affordabilityRatio: 'Affordability ratio (disposable / required)',
      feasibilityStatusLabel: 'Feasibility status',
      liveModelComparison: 'Live model monthly estimate',
    },
  },
  fr: {
    title: "Moteur de planification d'objectifs",
    subtitle:
      "Repondez a des questions pratiques, obtenez une estimation detaillee et des recommandations concretes.",
    goalTypes: {
      retirement: 'Retraite',
      house: 'Achat de maison',
      education: 'Epargne etudes',
      emergency: "Fonds d'urgence",
      wealth: 'Accumulation de patrimoine',
    },
    riskOptions: ['Prudent', 'Modere', 'Dynamique'],
    periodOptions: {
      biweekly: 'Aux deux semaines',
      monthly: 'Mensuel',
      quarterly: 'Trimestriel',
      yearly: 'Annuel',
    },
    feasibilityStatus: {
      strong: 'Objectif fortement realisable avec votre budget actuel',
      tight: 'Objectif possible, mais serre sans ajustements',
      weak: 'Objectif peu realisable sans changements importants',
    },
    labels: {
      goalType: "Type d'objectif",
      profileTitle: "Profil de l'objectif",
      profileSubtitle: 'Choisissez un objectif puis repondez seulement aux questions utiles pour ce cas.',
      assumptionsTitle: 'Hypotheses de planification',
      assumptionsSubtitle: "Ces hypotheses influencent l'estimation et peuvent etre ajustees a tout moment.",
      currentAge: 'Age actuel',
      income: 'Revenu annuel',
      householdIncome: 'Revenu annuel du menage (optionnel)',
      savings: 'Epargne actuelle',
      investments: 'Placements actuels pour cet objectif',
      targetAmount: 'Montant cible',
      horizon: 'Horizon (annees)',
      yearsShort: 'ans',
      risk: 'Tolerance au risque',
      contributionPeriod: 'Frequence de cotisation',
      estimateNow: 'Estimer avec le modele en ligne',
      resultsTitle: "Resultats de l'estimation",
      resultsSubtitle: 'Chiffres detailles et recommandations strategiques selon votre scenario.',
      hasTfsa: 'Possede un CELI',
      hasRrsp: 'Possede un REER',
      hasFhsa: 'Possede un CELIAPP',
      hasResp: 'Possede un REEE',
      availableAccounts: 'Types de comptes disponibles',
      requiredMonthly: 'Epargne mensuelle equivalente requise',
      requiredPerPeriod: 'Epargne requise par periode choisie',
      progress: 'Progression actuelle',
      targetGoal: 'Montant cible',
      currentBase: 'Base actuelle vers cet objectif',
      projectedBase: "Valeur projetee de votre base actuelle a l'echeance",
      growthAssumption: 'Hypothese de croissance',
      gapAtStart: 'Ecart de financement actuel',
      accountSuggestions: 'Types de comptes suggeres',
      milestones: "Jalons d'epargne",
      strategy: 'Recommandations strategiques',
      assumptions: 'Hypotheses du modele',
      assumptionsLineOne: 'Suppose une croissance constante et des cotisations regulieres.',
      assumptionsLineTwo: 'Estimation educative uniquement; validez avec des professionnels qualifies.',
      accountLowLiquidity: 'Epargne a haute liquidite',
      accountNonRegistered: 'Compte non enregistre',
      detailsPrompt: 'Combinaison de comptes recommandee pour cet objectif.',
      milestonesSubtitle: 'Jalons cibles de 25 % a 100 % de realisation.',
      estimate: 'Estimer avec le modele en ligne',
      estimating: 'Estimation...',
      saveGoal: "Enregistrer l'objectif",
      savingGoal: 'Enregistrement...',
      saveSuccess: 'Objectif enregistre dans votre profil de planification.',
      saveLogin: 'Connectez-vous pour enregistrer vos objectifs.',
      apiError: 'Le modele de planification en ligne est indisponible. Estimation locale affichee.',
      resultLead: 'Cible de cotisation estimee',
      homeCost: 'Prix de la maison',
      downPaymentPct: 'Pourcentage de mise de fonds',
      downPaymentAmount: 'Mise de fonds estimee',
      mortgageRate: 'Taux hypothecaire (%)',
      amortizationYears: 'Amortissement hypothecaire (annees)',
      estimatedMortgagePayment: 'Paiement hypothecaire mensuel estime',
      educationAnnualCost: "Cout annuel estime des etudes (aujourd'hui)",
      educationYearsUntilStart: 'Annees avant le debut des etudes',
      educationYears: 'Duree des etudes (annees)',
      educationInflation: "Hypothese d'inflation des etudes (%)",
      expectedGrants: 'Subventions / REEE attendus',
      inflationAdjustedEducationCost: "Cout des etudes ajuste a l'inflation",
      monthlyEssentialExpenses: 'Depenses mensuelles essentielles',
      emergencyMonths: 'Couverture visee (mois)',
      emergencyBuildYears: 'Annees pour constituer ce fonds',
      desiredRetirementIncome: 'Revenu mensuel souhaite a la retraite',
      expectedGovBenefits: 'Revenu mensuel prevu RPC/SV/SRG',
      safeWithdrawalRate: 'Taux de retrait en retraite (%)',
      optionalFeasibility: "Evaluer la faisabilite de l'objectif (optionnel)",
      optionalFeasibilitySubtitle:
        'Ajoutez des donnees de budget pour une verification pratique de faisabilite et des recommandations.',
      monthlyExpenses: 'Depenses mensuelles totales',
      monthlyDebtPayments: 'Paiements mensuels de dettes',
      useHouseholdIncome: 'Utiliser le revenu du menage pour la faisabilite',
      feasibilityTitle: 'Analyse de faisabilite',
      feasibilitySubtitle: 'Evalue si votre budget actuel peut soutenir cet objectif de cotisation.',
      disposableIncome: 'Revenu disponible mensuel estime',
      affordabilityRatio: 'Ratio de capacite (disponible / requis)',
      feasibilityStatusLabel: 'Statut de faisabilite',
      liveModelComparison: 'Estimation mensuelle du modele en ligne',
    },
  },
};

const riskReturnMap = {
  Conservative: 4,
  Moderate: 5.5,
  Aggressive: 7,
  Prudent: 4,
  Modere: 5.5,
  Dynamique: 7,
};

const localizeFrenchAcronyms = (value, lang) => {
  if (lang !== 'fr') return value;
  if (value === 'RRSP') return 'REER';
  if (value === 'TFSA') return 'CELI';
  if (value === 'FHSA') return 'CELIAPP';
  if (value === 'RESP') return 'REEE';
  return value;
};

const GOAL_IDS = ['house', 'education', 'emergency', 'wealth'];

const PERIODS_PER_YEAR = {
  biweekly: 26,
  monthly: 12,
  quarterly: 4,
  yearly: 1,
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const calculatePerPeriodContribution = ({ targetAmount, currentBase, annualRate, years, contributionPeriod }) => {
  const horizonYears = Math.max(1, toNumber(years, 1));
  const goalTarget = Math.max(0, toNumber(targetAmount, 0));
  const baseNow = Math.max(0, toNumber(currentBase, 0));
  const growthRate = Math.max(-0.05, toNumber(annualRate, 0.05));
  const periodsPerYear = PERIODS_PER_YEAR[contributionPeriod] || PERIODS_PER_YEAR.monthly;
  const totalPeriods = Math.max(1, Math.round(horizonYears * periodsPerYear));
  const periodicRate = growthRate / periodsPerYear;

  const projectedBaseAtHorizon = baseNow * Math.pow(1 + growthRate, horizonYears);
  const remainingTarget = Math.max(0, goalTarget - projectedBaseAtHorizon);

  let requiredPerPeriod = 0;
  if (remainingTarget > 0) {
    if (periodicRate > 0) {
      const factor = (Math.pow(1 + periodicRate, totalPeriods) - 1) / periodicRate;
      requiredPerPeriod = factor > 0 ? remainingTarget / factor : remainingTarget / totalPeriods;
    } else {
      requiredPerPeriod = remainingTarget / totalPeriods;
    }
  }

  return {
    goalTarget,
    baseNow,
    projectedBaseAtHorizon,
    remainingTarget,
    requiredPerPeriod,
    requiredMonthlyEquivalent: requiredPerPeriod * (periodsPerYear / 12),
    progressPct: goalTarget > 0 ? Math.min(100, (baseNow / goalTarget) * 100) : 0,
    horizonYears,
    growthRate,
  };
};

const estimateMonthlyMortgagePayment = ({ homeCost, downPaymentAmount, mortgageRate, amortizationYears }) => {
  const principal = Math.max(0, toNumber(homeCost, 0) - toNumber(downPaymentAmount, 0));
  const monthlyRate = Math.max(0, toNumber(mortgageRate, 0) / 100) / 12;
  const months = Math.max(12, Math.round(Math.max(1, toNumber(amortizationYears, 25)) * 12));
  if (principal <= 0) return 0;
  if (monthlyRate === 0) return principal / months;
  return (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
};

const formatMoney = (value, lang) =>
  new Intl.NumberFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);

const buildStrategyTips = (goalId, model, lang) => {
  const tips = [];
  if (goalId === 'house') {
    tips.push(lang === 'fr' ? 'Priorisez une mise de fonds stable a mesure que la date approche.' : 'Prioritize a stable down-payment bucket as your purchase date gets closer.');
    tips.push(lang === 'fr' ? 'Testez le budget avec un taux hypothecaire plus eleve.' : 'Stress-test affordability with a mortgage rate slightly above your base assumption.');
  } else if (goalId === 'education') {
    tips.push(lang === 'fr' ? 'Commencez tot pour capter plus de subventions.' : 'Start early to capture more grants and reduce late-stage contribution pressure.');
    tips.push(lang === 'fr' ? 'Revisez annuellement le cout projete des etudes.' : 'Re-estimate projected education costs annually using updated assumptions.');
  } else if (goalId === 'emergency') {
    tips.push(lang === 'fr' ? 'Constituez une reserve de base puis augmentez graduellement.' : 'Build a starter emergency layer first, then scale toward full coverage.');
    tips.push(lang === 'fr' ? 'Gardez ce fonds tres liquide et peu volatil.' : 'Keep emergency reserves in highly liquid, low-volatility vehicles.');
  } else if (goalId === 'retirement') {
    tips.push(lang === 'fr' ? 'Ajustez la cible selon les revenus publics attendus.' : 'Adjust retirement target as government income assumptions evolve.');
    tips.push(lang === 'fr' ? 'Augmentez les cotisations lors des hausses de revenu.' : 'Increase contributions gradually whenever income rises.');
  } else {
    tips.push(lang === 'fr' ? 'Decoupez l objectif en jalons de 12 mois.' : 'Break the goal into 12-month milestones to keep execution realistic.');
    tips.push(lang === 'fr' ? 'Automatisez les cotisations apres la paie.' : 'Automate contributions immediately after paydays to reduce friction.');
  }
  if (model.requiredMonthlyEquivalent > 0) {
    tips.push(lang === 'fr'
      ? `Besoin mensuel equivalent estime: ${formatMoney(model.requiredMonthlyEquivalent, lang)}.`
      : `Estimated monthly-equivalent need: ${formatMoney(model.requiredMonthlyEquivalent, lang)}.`);
  }
  return tips;
};

const buildFeasibility = ({ enabled, incomeAnnual, householdIncomeAnnual, useHouseholdIncome, monthlyExpenses, monthlyDebtPayments, requiredMonthlyEquivalent }, c) => {
  if (!enabled) return null;
  const incomeBaseAnnual = useHouseholdIncome ? Math.max(toNumber(householdIncomeAnnual, 0), toNumber(incomeAnnual, 0)) : toNumber(incomeAnnual, 0);
  const disposableMonthly = Math.max(0, incomeBaseAnnual / 12 - Math.max(0, toNumber(monthlyExpenses, 0)) - Math.max(0, toNumber(monthlyDebtPayments, 0)));
  const needed = Math.max(1, toNumber(requiredMonthlyEquivalent, 0));
  const ratio = disposableMonthly / needed;

  let statusKey = 'weak';
  if (ratio >= 1.25) statusKey = 'strong';
  else if (ratio >= 0.9) statusKey = 'tight';

  return {
    disposableMonthly,
    ratio,
    statusLabel: c.feasibilityStatus[statusKey],
    recommendations:
      statusKey === 'strong'
        ? [
            c.labels.resultLead,
            c.labels.assumptionsLineTwo,
          ]
        : statusKey === 'tight'
          ? [
              c.labels.optionalFeasibilitySubtitle,
              c.labels.assumptionsLineTwo,
            ]
          : [
              c.labels.apiError,
              c.labels.assumptionsLineTwo,
            ],
  };
};

const GoalPlanningEngine = ({ apiBase, authUser }) => {
  const { lang } = useLanguage();
  const c = COPY[lang] || COPY.en;

  const [inputs, setInputs] = useState({
    goalId: 'house',
    currentAge: 34,
    income: 95000,
    householdIncome: 130000,
    savings: 12000,
    investments: 18000,
    targetAmount: 350000,
    horizon: 15,
    risk: c.riskOptions[1],
    contributionPeriod: 'monthly',
    hasTfsa: true,
    hasRrsp: true,
    hasFhsa: true,
    hasResp: false,
    homeCost: 700000,
    downPaymentPct: 20,
    mortgageRate: 4.9,
    amortizationYears: 25,
    educationAnnualCost: 18000,
    educationYearsUntilStart: 8,
    educationYears: 4,
    educationInflation: 3,
    expectedGrants: 7000,
    monthlyEssentialExpenses: 4500,
    emergencyMonths: 6,
    emergencyBuildYears: 2,
    desiredRetirementIncome: 6500,
    expectedGovBenefits: 2200,
    safeWithdrawalRate: 4,
    enableFeasibility: false,
    useHouseholdIncome: true,
    monthlyExpenses: 6200,
    monthlyDebtPayments: 900,
  });

  const [results, setResults] = useState(null);
  const [liveModelSnapshot, setLiveModelSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const activeGoalLabel = c.goalTypes[inputs.goalId] || c.goalTypes.house;

  const localModel = useMemo(() => {
    const currentBase = Number(inputs.savings) + Number(inputs.investments);
    const annualRate = (riskReturnMap[inputs.risk] ?? 5) / 100;
    const period = inputs.contributionPeriod;

    let goalTarget = Math.max(0, toNumber(inputs.targetAmount, 0));
    let horizonYears = Math.max(1, toNumber(inputs.horizon, 1));
    let extras = {};

    if (inputs.goalId === 'house') {
      goalTarget = Math.max(0, toNumber(inputs.homeCost, 0) * Math.max(0, toNumber(inputs.downPaymentPct, 0)) / 100);
      extras = {
        homeCost: Math.max(0, toNumber(inputs.homeCost, 0)),
        downPaymentAmount: goalTarget,
        estimatedMortgagePayment: estimateMonthlyMortgagePayment({
          homeCost: inputs.homeCost,
          downPaymentAmount: goalTarget,
          mortgageRate: inputs.mortgageRate,
          amortizationYears: inputs.amortizationYears,
        }),
      };
    } else if (inputs.goalId === 'education') {
      horizonYears = Math.max(1, toNumber(inputs.educationYearsUntilStart, 1));
      const educationYears = Math.max(1, toNumber(inputs.educationYears, 1));
      const annualCostToday = Math.max(0, toNumber(inputs.educationAnnualCost, 0));
      const inflation = Math.max(0, toNumber(inputs.educationInflation, 0)) / 100;
      const totalTodayCost = annualCostToday * educationYears;
      const inflationAdjustedCost = totalTodayCost * Math.pow(1 + inflation, horizonYears);
      const expectedGrants = Math.max(0, toNumber(inputs.expectedGrants, 0));
      goalTarget = Math.max(0, inflationAdjustedCost - expectedGrants);
      extras = {
        inflationAdjustedEducationCost: inflationAdjustedCost,
        expectedGrants,
      };
    } else if (inputs.goalId === 'emergency') {
      goalTarget = Math.max(0, toNumber(inputs.monthlyEssentialExpenses, 0) * Math.max(1, toNumber(inputs.emergencyMonths, 1)));
      horizonYears = Math.max(1, toNumber(inputs.emergencyBuildYears, 1));
    } else if (inputs.goalId === 'retirement') {
      const desiredMonthlyIncome = Math.max(0, toNumber(inputs.desiredRetirementIncome, 0));
      const govBenefitsMonthly = Math.max(0, toNumber(inputs.expectedGovBenefits, 0));
      const withdrawalRate = Math.max(1, toNumber(inputs.safeWithdrawalRate, 4)) / 100;
      const annualPortfolioNeed = Math.max(0, (desiredMonthlyIncome - govBenefitsMonthly) * 12);
      goalTarget = annualPortfolioNeed / withdrawalRate;
      extras = { annualPortfolioNeed };
    }

    const contributionPlan = calculatePerPeriodContribution({
      targetAmount: goalTarget,
      currentBase,
      annualRate,
      years: horizonYears,
      contributionPeriod: period,
    });

    const accountSuggestions = [];
    if (inputs.goalId === 'house') {
      accountSuggestions.push('FHSA', 'TFSA');
    } else if (inputs.goalId === 'retirement') {
      accountSuggestions.push('RRSP', 'TFSA');
    } else if (inputs.goalId === 'education') {
      accountSuggestions.push('RESP', 'TFSA');
    } else if (inputs.goalId === 'emergency') {
      accountSuggestions.push('TFSA', c.labels.accountLowLiquidity);
    } else {
      accountSuggestions.push('TFSA', c.labels.accountNonRegistered);
    }

    if (!inputs.hasTfsa) {
      const idx = accountSuggestions.indexOf('TFSA');
      if (idx >= 0) accountSuggestions.splice(idx, 1);
    }
    if (!inputs.hasRrsp) {
      const idx = accountSuggestions.indexOf('RRSP');
      if (idx >= 0) accountSuggestions.splice(idx, 1);
    }
    if (!inputs.hasFhsa) {
      const idx = accountSuggestions.indexOf('FHSA');
      if (idx >= 0) accountSuggestions.splice(idx, 1);
    }
    if (!inputs.hasResp) {
      const idx = accountSuggestions.indexOf('RESP');
      if (idx >= 0) accountSuggestions.splice(idx, 1);
    }

    return {
      goalId: inputs.goalId,
      ...contributionPlan,
      accountSuggestions,
      annualRate,
      strategy: buildStrategyTips(inputs.goalId, contributionPlan, lang),
      feasibility: buildFeasibility({
        enabled: Boolean(inputs.enableFeasibility),
        incomeAnnual: inputs.income,
        householdIncomeAnnual: inputs.householdIncome,
        useHouseholdIncome: inputs.useHouseholdIncome,
        monthlyExpenses: inputs.monthlyExpenses,
        monthlyDebtPayments: inputs.monthlyDebtPayments,
        requiredMonthlyEquivalent: contributionPlan.requiredMonthlyEquivalent,
      }, c),
      ...extras,
    };
  }, [inputs, c, lang]);

  const updateInput = (key, value) => {
    setResults(null);
    setLiveModelSnapshot(null);
    setErrorMessage('');
    setSaveMessage('');
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const buildApiPayload = (computedModel) => ({
    goal_type: activeGoalLabel,
    current_age: Number(inputs.currentAge),
    annual_income: Number(inputs.income),
    current_savings: Number(inputs.savings),
    current_investments: Number(inputs.investments),
    target_amount: Number(computedModel.goalTarget),
    horizon_years: Number(computedModel.horizonYears),
    risk_tolerance: inputs.risk,
    has_tfsa: Boolean(inputs.hasTfsa),
    has_rrsp: Boolean(inputs.hasRrsp),
    has_fhsa: Boolean(inputs.hasFhsa),
    has_resp: Boolean(inputs.hasResp),
  });

  const runEstimate = async () => {
    const computed = localModel;
    if (!apiBase) {
      setResults(computed);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const response = await axios.post(`${apiBase}/planning/goals/estimate`, buildApiPayload(computed), {
        withCredentials: true,
      });
      const apiModel = response?.data || null;
      if (apiModel && Number.isFinite(Number(apiModel.required_monthly_savings))) {
        setLiveModelSnapshot({
          requiredMonthly: Number(apiModel.required_monthly_savings),
        });
      }
      setResults(computed);
    } catch (_error) {
      setLiveModelSnapshot(null);
      setResults(computed);
      setErrorMessage(c.labels.apiError);
    } finally {
      setLoading(false);
    }
  };

  const saveGoal = async () => {
    if (!authUser || !apiBase) {
      setSaveMessage(c.labels.saveLogin);
      return;
    }

    setSaving(true);
    setSaveMessage('');
    setErrorMessage('');
    try {
      const computed = results || localModel;
      const payload = {
        ...buildApiPayload(computed),
        goal_name: `${activeGoalLabel} (${Math.round(computed.horizonYears)}y)`,
      };
      await axios.post(`${apiBase}/planning/goals`, payload, { withCredentials: true });
      setSaveMessage(c.labels.saveSuccess);
    } catch (_error) {
      setErrorMessage(c.labels.apiError);
    } finally {
      setSaving(false);
    }
  };

  const renderGoalSpecificFields = () => {
    if (inputs.goalId === 'house') {
      return (
        <div className="goal-form-grid">
          <label><span>{c.labels.homeCost}</span><input type="number" value={inputs.homeCost} onChange={(e) => updateInput('homeCost', Number(e.target.value))} min="0" step="1000" /></label>
          <label><span>{c.labels.downPaymentPct}</span><input type="number" value={inputs.downPaymentPct} onChange={(e) => updateInput('downPaymentPct', Number(e.target.value))} min="5" max="100" step="1" /></label>
          <label><span>{c.labels.horizon}</span><input type="number" value={inputs.horizon} onChange={(e) => updateInput('horizon', Number(e.target.value))} min="1" max="30" /></label>
          <label><span>{c.labels.mortgageRate}</span><input type="number" value={inputs.mortgageRate} onChange={(e) => updateInput('mortgageRate', Number(e.target.value))} min="0" max="15" step="0.1" /></label>
          <label><span>{c.labels.amortizationYears}</span><input type="number" value={inputs.amortizationYears} onChange={(e) => updateInput('amortizationYears', Number(e.target.value))} min="5" max="35" /></label>
        </div>
      );
    }
    if (inputs.goalId === 'education') {
      return (
        <div className="goal-form-grid">
          <label><span>{c.labels.educationAnnualCost}</span><input type="number" value={inputs.educationAnnualCost} onChange={(e) => updateInput('educationAnnualCost', Number(e.target.value))} min="0" step="500" /></label>
          <label><span>{c.labels.educationYearsUntilStart}</span><input type="number" value={inputs.educationYearsUntilStart} onChange={(e) => updateInput('educationYearsUntilStart', Number(e.target.value))} min="1" max="25" /></label>
          <label><span>{c.labels.educationYears}</span><input type="number" value={inputs.educationYears} onChange={(e) => updateInput('educationYears', Number(e.target.value))} min="1" max="8" /></label>
          <label><span>{c.labels.educationInflation}</span><input type="number" value={inputs.educationInflation} onChange={(e) => updateInput('educationInflation', Number(e.target.value))} min="0" max="10" step="0.1" /></label>
          <label><span>{c.labels.expectedGrants}</span><input type="number" value={inputs.expectedGrants} onChange={(e) => updateInput('expectedGrants', Number(e.target.value))} min="0" step="500" /></label>
        </div>
      );
    }
    if (inputs.goalId === 'emergency') {
      return (
        <div className="goal-form-grid">
          <label><span>{c.labels.monthlyEssentialExpenses}</span><input type="number" value={inputs.monthlyEssentialExpenses} onChange={(e) => updateInput('monthlyEssentialExpenses', Number(e.target.value))} min="0" step="100" /></label>
          <label><span>{c.labels.emergencyMonths}</span><input type="number" value={inputs.emergencyMonths} onChange={(e) => updateInput('emergencyMonths', Number(e.target.value))} min="1" max="24" /></label>
          <label><span>{c.labels.emergencyBuildYears}</span><input type="number" value={inputs.emergencyBuildYears} onChange={(e) => updateInput('emergencyBuildYears', Number(e.target.value))} min="1" max="10" /></label>
        </div>
      );
    }
    if (inputs.goalId === 'retirement') {
      return (
        <div className="goal-form-grid">
          <label><span>{c.labels.currentAge}</span><input type="number" value={inputs.currentAge} onChange={(e) => updateInput('currentAge', Number(e.target.value))} min="18" max="75" /></label>
          <label><span>{c.labels.horizon}</span><input type="number" value={inputs.horizon} onChange={(e) => updateInput('horizon', Number(e.target.value))} min="1" max="45" /></label>
          <label><span>{c.labels.desiredRetirementIncome}</span><input type="number" value={inputs.desiredRetirementIncome} onChange={(e) => updateInput('desiredRetirementIncome', Number(e.target.value))} min="0" step="100" /></label>
          <label><span>{c.labels.expectedGovBenefits}</span><input type="number" value={inputs.expectedGovBenefits} onChange={(e) => updateInput('expectedGovBenefits', Number(e.target.value))} min="0" step="100" /></label>
          <label><span>{c.labels.safeWithdrawalRate}</span><input type="number" value={inputs.safeWithdrawalRate} onChange={(e) => updateInput('safeWithdrawalRate', Number(e.target.value))} min="2" max="8" step="0.1" /></label>
        </div>
      );
    }
    return (
      <div className="goal-form-grid">
        <label><span>{c.labels.targetAmount}</span><input type="number" value={inputs.targetAmount} onChange={(e) => updateInput('targetAmount', Number(e.target.value))} min="0" step="1000" /></label>
        <label><span>{c.labels.horizon}</span><input type="number" value={inputs.horizon} onChange={(e) => updateInput('horizon', Number(e.target.value))} min="1" max="40" /></label>
      </div>
    );
  };

  const model = results || localModel;
  const liveModelDelta = liveModelSnapshot ? liveModelSnapshot.requiredMonthly - model.requiredMonthlyEquivalent : null;
  const liveModelDeltaPct = liveModelDelta !== null && model.requiredMonthlyEquivalent > 0
    ? (liveModelDelta / model.requiredMonthlyEquivalent) * 100
    : null;
  const liveModelExplanation = liveModelSnapshot
    ? lang === 'fr'
      ? `Le modele en ligne (${formatMoney(liveModelSnapshot.requiredMonthly, lang)}) est ${liveModelDelta >= 0 ? 'plus eleve' : 'plus bas'} que votre estimation mensuelle equivalente (${formatMoney(model.requiredMonthlyEquivalent, lang)}) de ${formatMoney(Math.abs(liveModelDelta), lang)}${liveModelDeltaPct !== null ? `, soit ${Math.abs(liveModelDeltaPct).toFixed(1)} %` : ''}.`
      : `The live model estimate (${formatMoney(liveModelSnapshot.requiredMonthly, lang)}) is ${liveModelDelta >= 0 ? 'higher' : 'lower'} than your monthly-equivalent estimate above (${formatMoney(model.requiredMonthlyEquivalent, lang)}) by ${formatMoney(Math.abs(liveModelDelta), lang)}${liveModelDeltaPct !== null ? `, or ${Math.abs(liveModelDeltaPct).toFixed(1)}%` : ''}.`
    : '';

  return (
    <div className="goal-root">
      <header className="goal-header dashboard-card">
        <div>
          <h2>{c.title}</h2>
          <p>{c.subtitle}</p>
        </div>
      </header>

      <ComplianceNotice
        enText="This platform provides educational information, financial modeling tools, and scenario analysis. It does not provide personalized financial advice, investment recommendations, tax advice, legal advice, or retirement advice."
        frText="Cette plateforme fournit des informations educatives, des outils de modelisation financiere et des analyses de scenarios. Elle ne fournit pas de conseils financiers personnalises, de recommandations de placement, de conseils fiscaux, juridiques ou de retraite."
      />

      <section className="goal-layout">
        <article className="goal-form dashboard-glass">
          <section className="goal-block">
            <h3>{c.labels.profileTitle}</h3>
            <p>{c.labels.profileSubtitle}</p>
            <div className="goal-form-grid">
              <label><span>{c.labels.goalType}</span>
                <select value={inputs.goalId} onChange={(e) => updateInput('goalId', e.target.value)}>
                  {GOAL_IDS.map((goalId) => (
                    <option key={goalId} value={goalId}>{c.goalTypes[goalId]}</option>
                  ))}
                </select>
              </label>
              <label><span>{c.labels.contributionPeriod}</span>
                <select value={inputs.contributionPeriod} onChange={(e) => updateInput('contributionPeriod', e.target.value)}>
                  {Object.entries(c.periodOptions).map(([periodId, label]) => (
                    <option key={periodId} value={periodId}>{label}</option>
                  ))}
                </select>
              </label>
              <label><span>{c.labels.risk}</span>
                <select value={inputs.risk} onChange={(e) => updateInput('risk', e.target.value)}>
                  {c.riskOptions.map((risk) => (
                    <option key={risk} value={risk}>{risk}</option>
                  ))}
                </select>
              </label>
              <label><span>{c.labels.income}</span><input type="number" value={inputs.income} onChange={(e) => updateInput('income', Number(e.target.value))} min="0" step="1000" /></label>
              <label><span>{c.labels.savings}</span><input type="number" value={inputs.savings} onChange={(e) => updateInput('savings', Number(e.target.value))} min="0" step="100" /></label>
              <label><span>{c.labels.investments}</span><input type="number" value={inputs.investments} onChange={(e) => updateInput('investments', Number(e.target.value))} min="0" step="100" /></label>
            </div>
          </section>

          <section className="goal-block">
            <h3>{activeGoalLabel}</h3>
            {renderGoalSpecificFields()}
          </section>

          <section className="goal-block">
            <h3>{c.labels.assumptionsTitle}</h3>
            <p>{c.labels.assumptionsSubtitle}</p>
            <div className="goal-checkbox-grid">
              <label><input type="checkbox" checked={inputs.hasTfsa} onChange={(e) => updateInput('hasTfsa', e.target.checked)} /><span>{c.labels.hasTfsa}</span></label>
              <label><input type="checkbox" checked={inputs.hasRrsp} onChange={(e) => updateInput('hasRrsp', e.target.checked)} /><span>{c.labels.hasRrsp}</span></label>
              <label><input type="checkbox" checked={inputs.hasFhsa} onChange={(e) => updateInput('hasFhsa', e.target.checked)} /><span>{c.labels.hasFhsa}</span></label>
              <label><input type="checkbox" checked={inputs.hasResp} onChange={(e) => updateInput('hasResp', e.target.checked)} /><span>{c.labels.hasResp}</span></label>
            </div>
          </section>

          <section className="goal-block optional">
            <label className="goal-optional-toggle">
              <input type="checkbox" checked={inputs.enableFeasibility} onChange={(e) => updateInput('enableFeasibility', e.target.checked)} />
              <span>{c.labels.optionalFeasibility}</span>
            </label>
            <p>{c.labels.optionalFeasibilitySubtitle}</p>
            {inputs.enableFeasibility && (
              <div className="goal-form-grid">
                <label><span>{c.labels.useHouseholdIncome}</span>
                  <select value={inputs.useHouseholdIncome ? 'yes' : 'no'} onChange={(e) => updateInput('useHouseholdIncome', e.target.value === 'yes')}>
                    <option value="yes">{lang === 'fr' ? 'Oui' : 'Yes'}</option>
                    <option value="no">{lang === 'fr' ? 'Non' : 'No'}</option>
                  </select>
                </label>
                <label><span>{c.labels.householdIncome}</span><input type="number" value={inputs.householdIncome} onChange={(e) => updateInput('householdIncome', Number(e.target.value))} min="0" step="1000" /></label>
                <label><span>{c.labels.monthlyExpenses}</span><input type="number" value={inputs.monthlyExpenses} onChange={(e) => updateInput('monthlyExpenses', Number(e.target.value))} min="0" step="100" /></label>
                <label><span>{c.labels.monthlyDebtPayments}</span><input type="number" value={inputs.monthlyDebtPayments} onChange={(e) => updateInput('monthlyDebtPayments', Number(e.target.value))} min="0" step="50" /></label>
              </div>
            )}
          </section>

          <div className="goal-actions">
            <button type="button" onClick={runEstimate} disabled={loading}>
              {loading ? c.labels.estimating : c.labels.estimateNow}
            </button>
            <button type="button" onClick={saveGoal} disabled={saving || !authUser} title={!authUser ? c.labels.saveLogin : c.labels.saveGoal}>
              {saving ? c.labels.savingGoal : c.labels.saveGoal}
            </button>
          </div>

          {errorMessage && <p className="goal-feedback error">{errorMessage}</p>}
          {saveMessage && <p className="goal-feedback success">{saveMessage}</p>}
        </article>

        {results && (
          <article className="goal-results dashboard-glass">
            <header className="goal-results-head">
              <h3>{c.labels.resultsTitle}</h3>
              <p>{c.labels.resultsSubtitle}</p>
            </header>

            <div className="goal-result-lead">
              <span>{c.labels.resultLead}</span>
              <strong>{formatMoney(model.requiredPerPeriod, lang)}</strong>
              <p>{activeGoalLabel} - {c.periodOptions[inputs.contributionPeriod]}</p>
            </div>

            <div className="goal-kpi-grid">
              <div><span>{c.labels.requiredPerPeriod}</span><strong>{formatMoney(model.requiredPerPeriod, lang)}</strong></div>
              <div><span>{c.labels.requiredMonthly}</span><strong>{formatMoney(model.requiredMonthlyEquivalent, lang)}</strong></div>
              <div><span>{c.labels.targetGoal}</span><strong>{formatMoney(model.goalTarget, lang)}</strong></div>
              <div><span>{c.labels.currentBase}</span><strong>{formatMoney(model.baseNow, lang)}</strong></div>
              <div><span>{c.labels.projectedBase}</span><strong>{formatMoney(model.projectedBaseAtHorizon, lang)}</strong></div>
              <div><span>{c.labels.gapAtStart}</span><strong>{formatMoney(model.remainingTarget, lang)}</strong></div>
            </div>

            <div className="goal-progress-bar" aria-label={c.labels.progress}>
              <div className="goal-progress-fill" style={{ width: `${Math.max(2, model.progressPct)}%` }} />
            </div>

            <div className="goal-inline-summary">
              <span>{c.labels.progress}: <strong>{model.progressPct.toFixed(1)}%</strong></span>
              <span>{c.labels.growthAssumption}: <strong>{(model.growthRate * 100).toFixed(1)}%</strong></span>
            </div>

            {inputs.goalId === 'house' && (
              <section className="goal-detail-panel">
                <h4>{lang === 'fr' ? 'Details achat immobilier' : 'Home purchase details'}</h4>
                <ul className="goal-list-panel">
                  <li><span>{c.labels.homeCost}</span><strong>{formatMoney(model.homeCost, lang)}</strong></li>
                  <li><span>{c.labels.downPaymentAmount}</span><strong>{formatMoney(model.downPaymentAmount, lang)}</strong></li>
                  <li><span>{c.labels.estimatedMortgagePayment}</span><strong>{formatMoney(model.estimatedMortgagePayment, lang)}</strong></li>
                </ul>
              </section>
            )}

            {inputs.goalId === 'education' && (
              <section className="goal-detail-panel">
                <h4>{lang === 'fr' ? 'Details etudes' : 'Education goal details'}</h4>
                <ul className="goal-list-panel">
                  <li><span>{c.labels.inflationAdjustedEducationCost}</span><strong>{formatMoney(model.inflationAdjustedEducationCost, lang)}</strong></li>
                  <li><span>{c.labels.expectedGrants}</span><strong>{formatMoney(model.expectedGrants, lang)}</strong></li>
                  <li><span>{c.labels.targetGoal}</span><strong>{formatMoney(model.goalTarget, lang)}</strong></li>
                </ul>
              </section>
            )}

            <section className="goal-detail-panel">
              <h4>{c.labels.accountSuggestions}</h4>
              <p>{c.labels.detailsPrompt}</p>
              <ul className="goal-list-panel bullets">
                {model.accountSuggestions.map((account) => (
                  <li key={account}>{localizeFrenchAcronyms(account, lang)}</li>
                ))}
              </ul>
            </section>

            <section className="goal-detail-panel">
              <h4>{c.labels.strategy}</h4>
              <ul className="goal-list-panel bullets">
                {model.strategy.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="goal-detail-panel assumptions">
              <h4>{c.labels.assumptions}</h4>
              <p>{c.labels.assumptionsLineOne}</p>
              <p>{c.labels.assumptionsLineTwo}</p>
            </section>

            {liveModelSnapshot && (
              <section className="goal-detail-panel live-note">
                <h4>{c.labels.liveModelComparison}</h4>
                <p>{formatMoney(liveModelSnapshot.requiredMonthly, lang)}</p>
                <p>{liveModelExplanation}</p>
              </section>
            )}

            {model.feasibility && (
              <section className="goal-detail-panel feasibility">
                <h4>{c.labels.feasibilityTitle}</h4>
                <p>{c.labels.feasibilitySubtitle}</p>
                <ul className="goal-list-panel">
                  <li><span>{c.labels.disposableIncome}</span><strong>{formatMoney(model.feasibility.disposableMonthly, lang)}</strong></li>
                  <li><span>{c.labels.affordabilityRatio}</span><strong>{model.feasibility.ratio.toFixed(2)}x</strong></li>
                  <li><span>{c.labels.feasibilityStatusLabel}</span><strong>{model.feasibility.statusLabel}</strong></li>
                </ul>
                <ul className="goal-list-panel bullets">
                  {model.feasibility.recommendations.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            )}
          </article>
        )}
      </section>
    </div>
  );
};

export default GoalPlanningEngine;
